import React, { useState, useEffect, useCallback, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";

export default function ConsumptionCalculatorDialog({
    open,
    onClose,
    deviceId,
    API_BASE,
}) {
    const { t, i18n } = useTranslation();
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [selectedRegister, setSelectedRegister] = useState("");
    const [registers, setRegisters] = useState([]);
    const [result, setResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const toKiloUnit = (unit = "") => {
        const normalized = unit.trim().toLowerCase();
        if (normalized === "wh") return "kWh";
        if (normalized === "varh") return "kVArh";
        if (normalized === "vah") return "kVAh";
        return unit;
    };

    const toKiloValue = (value, unit = "") => {
        const normalized = unit.trim().toLowerCase();
        if (["wh", "varh", "vah"].includes(normalized)) return Number(value) / 1000;
        return Number(value);
    };


    const fetchRegisters = useCallback(async () => {
        try {
            const lang = i18n.language.split('-')[0]; // tr-TR -> tr
            const res = await fetch(`${API_BASE}/reports/device-registers?deviceId=${deviceId}&lang=${lang}`);
            if (!res.ok) throw new Error("Failed to fetch registers");
            const data = await res.json();
            setRegisters(data);
        } catch (err) {
            console.error(err);
            setError(t('consumptionCalculator.errorRegisters'));
        }
    }, [API_BASE, deviceId, i18n.language, t]);

    useEffect(() => {
        if (open && deviceId) {
            fetchRegisters();
            setResult(null);
            setError("");
        }
    }, [open, deviceId, fetchRegisters]);

    const handleCalculate = async () => {
        if (!selectedRegister || !start || !end) {
            setError(t('consumptionCalculator.errorFillAll'));
            return;
        }

        setIsLoading(true);
        setError("");
        setResult(null);

        try {
            const lang = i18n.language.split('-')[0]; // tr-TR -> tr
            const res = await fetch(`${API_BASE}/reports/calculate-consumption`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    deviceId,
                    registerName: selectedRegister,
                    startDate: start,
                    endDate: end,
                    lang
                })
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || t('consumptionCalculator.errorCalculation'));
            }

            const data = await res.json();
            setResult(data);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const displayUnit = result ? toKiloUnit(result.unit) : "";
    const displayStart = result ? toKiloValue(result.start.value, result.unit) : 0;
    const displayEnd = result ? toKiloValue(result.end.value, result.unit) : 0;
    const displayConsumption = result ? toKiloValue(result.consumption, result.unit) : 0;

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-30" />
                </Transition.Child>

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-lg">
                        <Dialog.Title className="text-lg font-semibold text-gray-800 mb-4 flex justify-between items-center">
                            <span>{t('consumptionCalculator.title')}</span>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                                <span className="text-2xl">&times;</span>
                            </button>
                        </Dialog.Title>

                        <div className="space-y-4">
                            {/* Register Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('consumptionCalculator.energyType')}
                                </label>
                                <select
                                    value={selectedRegister}
                                    onChange={(e) => setSelectedRegister(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-sky-500 focus:border-sky-500"
                                >
                                    <option value="">{t('consumptionCalculator.select')}</option>
                                    {registers.map((reg) => (
                                        <option key={reg.name} value={reg.name}>
                                            {reg.description} ({toKiloUnit(reg.unit)})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Date Selection */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('consumptionCalculator.startTime')}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={start}
                                        onChange={(e) => setStart(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('consumptionCalculator.endTime')}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={end}
                                        onChange={(e) => setEnd(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                                    />
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
                                    {error}
                                </div>
                            )}

                            {/* Result Area */}
                            {result && (
                                <div className="bg-sky-50 p-4 rounded-md border border-sky-100 space-y-2">
                                    <h3 className="font-semibold text-sky-800 border-b border-sky-200 pb-1 mb-2">
                                        {t('consumptionCalculator.resultTitle')}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                        <div className="text-gray-600 flex items-center justify-between">
                                            <span>{t('consumptionCalculator.start')}:</span>
                                        </div>
                                        <div className="font-mono text-gray-800 text-right">
                                            {displayStart.toFixed(2)} {displayUnit}
                                        </div>

                                        <div className="text-gray-600 flex items-center justify-between">
                                            <span>{t('consumptionCalculator.end')}:</span>
                                        </div>
                                        <div className="font-mono text-gray-800 text-right">
                                            {displayEnd.toFixed(2)} {displayUnit}
                                        </div>

                                        <div className="col-span-2 border-t border-sky-200 my-1"></div>

                                        <div className="text-gray-700 font-bold pt-1">{t('consumptionCalculator.totalConsumption')}:</div>
                                        <div className="font-bold text-sky-700 font-mono text-lg text-right">
                                            {displayConsumption.toFixed(2)} {displayUnit}
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-400 mt-2 italic text-center">
                                        {new Date(result.start.time).toLocaleString()} — {new Date(result.end.time).toLocaleString()}
                                    </div>
                                </div>
                            )}

                            {/* Buttons */}
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    {t('consumptionCalculator.closeButton')}
                                </button>
                                <button
                                    onClick={handleCalculate}
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isLoading && (
                                        <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                        </svg>
                                    )}
                                    {t('consumptionCalculator.calculateButton')}
                                </button>
                            </div>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </Transition>
    );
}
